"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Phone, ArrowRight, Search, CircleCheck as CheckCircle2, FileText, Wrench, TriangleAlert as AlertTriangle, Flame, Droplets, RotateCcw, Zap, Layers, Activity, MapPin, Clock, ShieldAlert } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
export default function Story() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: (custom: number = 0) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        delay: custom * 0.07,
        ease: "easeOut"
      }
    })
  };
  const approachSteps = [{
    step: "01",
    icon: Search,
    title: "Understand the Problem",
    description: "We listen to what you are experiencing over the phone and immediately gather the critical details of your plumbing emergency."
  }, {
    step: "02",
    icon: Activity,
    title: "Identify the Issue",
    description: "Our technician conducts an on-site physical diagnostic to locate the exact cause of the leak, clog, or mechanical breakdown."
  }, {
    step: "03",
    icon: FileText,
    title: "Explain the Solution",
    description: "We outline the necessary repair options in plain language with upfront pricing before any physical work starts on your property."
  }, {
    step: "04",
    icon: Wrench,
    title: "Complete the Authorized Work",
    description: "Once approved, we carry out the precise repair or component replacement, test the system thoroughly, and clean up the work area."
  }];
  const services = [{
    icon: AlertTriangle,
    title: "Emergency Plumbing",
    description: "Fast-response troubleshooting for active leaks, burst pipes, and sudden plumbing failures.",
    link: "/emergency-plumbing/"
  }, {
    icon: Droplets,
    title: "Drain Cleaning",
    description: "Thorough clearing of stubborn kitchen, bathroom, and main floor line blockages.",
    link: "/drain-cleaning/"
  }, {
    icon: Flame,
    title: "Water Heater Repair",
    description: "Targeted diagnostics and heating element repairs for gas and electric water heaters.",
    link: "/water-heater-repair/"
  }, {
    icon: RotateCcw,
    title: "Water Heater Replacement",
    description: "Professional tank and tankless system installation sized accurately for your household.",
    link: "/water-heater-replacement/"
  }, {
    icon: Zap,
    title: "Leak Detection & Repair",
    description: "Pinpointing hidden slab, wall, and fixture leaks to stop water loss and structural damage.",
    link: "/leak-detection-repair/"
  }, {
    icon: ShieldAlert,
    title: "Toilet Repair",
    description: "Resolving continuous running, severe clogs, base leaks, and internal valve issues.",
    link: "/toilet-repair/"
  }, {
    icon: Layers,
    title: "Sewer Line Repair",
    description: "Diagnosing and addressing sewer backups, line cracks, and underground flow issues.",
    link: "/sewer-line-repair/"
  }, {
    icon: Wrench,
    title: "Pipe Repair",
    description: "Repairs and localized repiping for copper, PEX, CPVC, and galvanized pipe sections.",
    link: "/pipe-repair/"
  }];
  return <section id="about-us" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 lg:py-28 overflow-hidden">
      <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-[1240px]">
        {/* ========================================================= */}
        {/* MODULE 1: HERO MODULE */}
        {/* ========================================================= */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center mb-20 md:mb-28">
          <motion.div className="lg:col-span-7 flex flex-col gap-5" variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} custom={0}>
            <div className="inline-flex items-center gap-2 self-start rounded-lg bg-secondary px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-secondary-foreground">
              <span className="inline-block size-2 rounded-lg bg-accent animate-pulse" />
              Jonesboro, GA Local Emergency Team
            </div>

            <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground leading-[1.12]">
              About Jonesboro Emergency Plumber
            </h1>

            <p className="font-default text-lg sm:text-xl text-foreground/90 font-medium leading-relaxed">
              We provide prompt, direct-dispatch emergency plumbing services across Jonesboro and Clayton County, focusing on clear communication and practical repairs when home plumbing fails.
            </p>

            <p className="font-default text-base text-muted-foreground leading-relaxed">
              When water lines break, drains overflow, or water heaters malfunction, homeowners need an immediate response. We prioritize direct telephone contact so you can describe your issue to a knowledgeable dispatcher and get a technician dispatched without delay.
            </p>

            {/* CTAs */}
            <div className="pt-2 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5">
              <Button asChild className="h-12 rounded-lg bg-accent px-7 text-accent-foreground font-bold shadow-md hover:brightness-105 transition-[filter,box-shadow]">
                <a href="tel:4708479076" className="flex items-center justify-center gap-2.5">
                  <Phone className="size-5 shrink-0" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>

              <Button asChild variant="outline" className="h-12 rounded-lg border-border bg-card px-6 font-semibold text-foreground hover:bg-muted transition-[background-color,border-color]">
                <Link to="/contact/" className="flex items-center justify-center gap-2">
                  <span>Request Service</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Technician Image */}
          <motion.div className="lg:col-span-5" variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} custom={1}>
            <div className="group relative rounded-xl overflow-hidden border border-border shadow-xl bg-card">
              <AspectRatio ratio={4 / 3} className="bg-muted overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/technician_plumbing_inspection.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/technician_plumbing_inspection.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/technician_plumbing_inspection.webp-wvc-sizes" alt="Jonesboro plumbing technician conducting an on-site diagnostic inspection" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 ease-out group-hover:scale-105" />
              </AspectRatio>

              {/* Factual Support Badge */}
              <div className="absolute bottom-4 left-4 right-4 bg-background/95 backdrop-blur-md rounded-lg p-4 border border-border shadow-md flex items-center gap-3.5">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <CheckCircle2 className="size-5 text-accent" />
                </div>
                <div>
                  <div className="font-default text-sm font-bold text-foreground">
                    Direct Local Dispatch
                  </div>
                  <div className="font-default text-xs text-muted-foreground">
                    Serving Jonesboro and neighboring Clayton County communities
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* ========================================================= */}
        {/* MODULE 2: WHO WE ARE */}
        {/* ========================================================= */}
        <div className="mb-20 md:mb-28">
          <motion.div className="max-w-3xl mb-10" variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} custom={0}>
            <div className="text-xs font-bold uppercase tracking-wider text-accent mb-2">
              Our Service Area & Operation
            </div>
            <h2 className="font-default text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              Local Plumbing Service in Jonesboro, GA
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <motion.div className="lg:col-span-7 flex flex-col gap-5 text-muted-foreground leading-relaxed" variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} custom={1}>
              <p>
                Jonesboro Emergency Plumber operates locally within Jonesboro, Georgia, and extends services to nearby Clayton County neighborhoods including Riverdale, Morrow, Forest Park, Stockbridge, Lovejoy, and Ellenwood. We focus on residential plumbing needs, offering practical solutions when unexpected leaks or backups disrupt your daily routine.
              </p>
              <p>
                Our scope of work spans urgent pipe repairs, fixture troubleshooting, water heater servicing, and drain clearing. We keep our operation streamlined to ensure fast dispatch times, straightforward diagnostic assessments, and honest recommendations suited to the specific plumbing infrastructure found in Georgia homes.
              </p>
              <p>
                We believe in treating every property with respect. Our technicians take time to explain what caused the failure, provide honest pricing options before starting, and ensure the work area is properly cleaned once repairs are finalized.
              </p>
            </motion.div>

            <motion.div className="lg:col-span-5 grid grid-cols-1 gap-4" variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.2
          }} custom={2}>
              <div className="rounded-xl border border-border bg-card p-5 shadow-xs flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-card-foreground mb-1">
                    Clayton County Coverage
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Active service coverage across Jonesboro, Riverdale, Morrow, Forest Park, and surrounding areas.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card p-5 shadow-xs flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                  <Clock className="size-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-card-foreground mb-1">
                    Direct Phone Contact
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Connect straight to phone dispatch without navigation queues or slow web-form delays.
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-card p-5 shadow-xs flex items-start gap-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
                  <FileText className="size-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-card-foreground mb-1">
                    Transparent Estimates
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Upfront diagnostic evaluations with clear price quotes prior to the start of any repair work.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ========================================================= */}
        {/* MODULE 3: OUR APPROACH */}
        {/* ========================================================= */}
        <div className="mb-20 md:mb-28">
          <motion.div className="text-center max-w-2xl mx-auto mb-12" variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} custom={0}>
            <div className="text-xs font-bold uppercase tracking-wider text-accent mb-2">
              Structured Diagnostic Process
            </div>
            <h2 className="font-default text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-3">
              We Start With the Plumbing Problem
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground">
              Every service visit follows a systematic four-step procedure designed to resolve your issue safely and efficiently.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {approachSteps.map((item, i) => {
            const StepIcon = item.icon;
            return <motion.div key={item.step} data-index={i} variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
              once: true,
              amount: 0.15
            }} custom={i + 1} className="h-full">
                  <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/40 transition-[border-color,box-shadow] hover:shadow-md relative overflow-hidden flex flex-col justify-between">
                    <div className="absolute top-0 left-0 right-0 h-1 bg-accent" />
                    <CardContent className="p-6 flex flex-col gap-4">
                      <div className="flex items-center justify-between">
                        <div className="flex size-11 items-center justify-center rounded-lg bg-secondary text-primary">
                          <StepIcon className="size-5" />
                        </div>
                        <span className="font-mono text-2xl font-bold text-muted-foreground/40">
                          {item.step}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-default text-lg font-bold text-card-foreground mb-2">
                          {item.title}
                        </h3>
                        <p className="font-default text-sm text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ========================================================= */}
        {/* MODULE 4: WHAT WE DO */}
        {/* ========================================================= */}
        <div>
          <motion.div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12" variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} custom={0}>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-accent mb-2">
                Complete Scope of Work
              </div>
              <h2 className="font-default text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
                What We Do
              </h2>
            </div>
            <p className="text-sm sm:text-base text-muted-foreground max-w-md">
              Reliable plumbing repair, maintenance, and diagnostic services tailored to Jonesboro households.
            </p>
          </motion.div>

          {/* 8-Card Service Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-10">
            {services.map((service, i) => {
            const ServiceIcon = service.icon;
            return <motion.div key={service.title} data-index={i} variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
              once: true,
              amount: 0.1
            }} custom={i % 4} className="h-full">
                  <Card className="h-full border border-border bg-card shadow-xs hover:border-primary/50 transition-[border-color,box-shadow] hover:shadow-md flex flex-col justify-between">
                    <CardContent className="p-6 flex flex-col gap-4">
                      <div className="flex size-11 items-center justify-center rounded-lg bg-secondary text-primary">
                        <ServiceIcon className="size-5" />
                      </div>
                      <div>
                        <h3 className="font-default text-lg font-bold text-card-foreground mb-1.5">
                          {service.title}
                        </h3>
                        <p className="font-default text-sm text-muted-foreground leading-relaxed mb-4">
                          {service.description}
                        </p>
                      </div>
                      <Link to={service.link} className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent transition-colors">
                        <span>Learn more</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>

          {/* View All Services Button */}
          <motion.div className="text-center pt-4" variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} custom={1}>
            <Button asChild className="h-11 rounded-lg bg-primary px-8 text-primary-foreground font-semibold shadow-sm hover:bg-primary/90 transition-colors">
              <Link to="/services/" className="inline-flex items-center gap-2">
                <span>View All Plumbing Services</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>;
}